/**
 * Marketing Landing Page
 * Displays the SolasLite marketing content
 */

import React, { useState, useEffect } from 'react';

const Marketing: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Load HubSpot form script when modal opens
  useEffect(() => {
    if (isModalOpen) {
      // Load HubSpot script if not already loaded
      const existingScript = document.querySelector('script[src*="js.hsforms.net/forms/embed/49566702.js"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://js.hsforms.net/forms/embed/49566702.js';
        script.defer = true;
        document.body.appendChild(script);
      }
    }
  }, [isModalOpen]);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className="py-8 px-4 bg-gray-100">
      <style>{`
        body {
          font-family: 'Inter', sans-serif;
          background-color: #f3f4f6;
        }
        .flyer-container {
          max-width: 1400px;
          width: 85%;
          margin: 0 auto;
          background: white;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          overflow: hidden;
        }
        @media (max-width: 768px) {
          .flyer-container {
            width: 95%;
          }
        }
        .hero-bg {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          position: relative;
        }
        .hero-pattern {
          background-image: radial-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px);
          background-size: 20px 20px;
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          opacity: 0.3;
        }
        .check-list li {
          position: relative;
          padding-left: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .check-list li::before {
          content: "✓";
          position: absolute;
          left: 0;
          color: #10b981;
          font-weight: bold;
        }
        .cross-list li {
          position: relative;
          padding-left: 1.5rem;
          margin-bottom: 0.5rem;
          color: #64748b;
        }
        .cross-list li::before {
          content: "✕";
          position: absolute;
          left: 0;
          color: #ef4444;
          font-weight: bold;
        }
      `}</style>

      {/* Flyer Container */}
      <div className="flyer-container rounded-lg">
        
        {/* Header / Hero */}
        <header className="hero-bg text-white p-12 md:p-16 lg:p-20 relative">
          <div className="hero-pattern"></div>
          
          {/* Login button - top right */}
          <a 
            href="https://solas-os.ai/login?final_redirect_url=https%3A%2F%2Flite.solas-os.ai%2Fdashboard"
            className="absolute top-6 right-6 md:top-8 md:right-8 bg-white text-slate-900 hover:bg-blue-50 font-semibold py-2 px-6 rounded-full transition-colors duration-200 z-20 flex items-center gap-2"
          >
            <i className="fas fa-sign-in-alt"></i>
            <span>Login</span>
          </a>
          
          <div className="relative z-10">
            <div className="flex justify-between items-center mb-10 md:mb-12">
              <img src="https://storage.googleapis.com/solas-images/SolasOS%20logo_B2.jpg" alt="SolasOS Logo" className="h-20 md:h-28 lg:h-32 rounded-lg" />
              <img src="https://storage.googleapis.com/solas-images/Untitled%20design%20(1).svg" alt="ISO Certified Badge" className="h-20 md:h-28 lg:h-32" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">Leadership Development Starts with Self-Understanding</h1>
            <p className="text-lg md:text-xl lg:text-2xl text-blue-100 max-w-4xl leading-relaxed">
              Organisations invest heavily in leadership development, yet progress often stalls because leaders lack baseline clarity. SolasLite anchors development in reality, not assumption.
            </p>
          </div>
        </header>

        {/* Section: The Real Issue (Problem Statement) */}
        <section className="p-12 md:p-16 lg:p-20 bg-slate-50 border-b border-gray-200">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 text-center">The Real Issue with Leadership Development</h2>
          <p className="text-center text-gray-600 text-base md:text-lg mb-10 md:mb-12 max-w-4xl mx-auto">Most leaders are expected to develop without a clear, objective understanding of their current reality:</p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10">
            {/* Point 1 */}
            <div className="text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-white rounded-xl shadow-sm border border-gray-100 flex items-center justify-center mx-auto mb-4 text-red-500">
                <i className="fas fa-eye-slash text-2xl md:text-3xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 text-base md:text-lg mb-2">Current Style</h3>
              <p className="text-sm md:text-base text-gray-500 leading-snug">Unaware of how they currently lead.</p>
            </div>
            {/* Point 2 */}
            <div className="text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-white rounded-xl shadow-sm border border-gray-100 flex items-center justify-center mx-auto mb-4 text-red-500">
                <i className="fas fa-comments text-2xl md:text-3xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 text-base md:text-lg mb-2">Communication</h3>
              <p className="text-sm md:text-base text-gray-500 leading-snug">Unclear impact on others.</p>
            </div>
            {/* Point 3 */}
            <div className="text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-white rounded-xl shadow-sm border border-gray-100 flex items-center justify-center mx-auto mb-4 text-red-500">
                <i className="fas fa-compress-arrows-alt text-2xl md:text-3xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 text-base md:text-lg mb-2">Under Pressure</h3>
              <p className="text-sm md:text-base text-gray-500 leading-snug">How behaviour shifts when stressed.</p>
            </div>
            {/* Point 4 */}
            <div className="text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-white rounded-xl shadow-sm border border-gray-100 flex items-center justify-center mx-auto mb-4 text-red-500">
                <i className="fas fa-search text-2xl md:text-3xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 text-base md:text-lg mb-2">Real Gaps</h3>
              <p className="text-sm md:text-base text-gray-500 leading-snug">Genuine development needs are hidden.</p>
            </div>
          </div>
        </section>

        {/* Section: How It Works */}
        <section className="p-12 md:p-16 lg:p-20 bg-white border-b border-gray-100">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-10 md:mb-14 text-center">How SolasLite Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12 text-center">
            {/* Step 1 */}
            <div>
              <div className="w-14 h-14 md:w-16 md:h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-5 text-xl md:text-2xl font-bold">1</div>
              <h3 className="font-bold text-gray-900 text-lg md:text-xl mb-3">Real Communication</h3>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed">Participants submit a representative set of everyday workplace messages they have already sent.</p>
            </div>
            {/* Step 2 */}
            <div>
              <div className="w-14 h-14 md:w-16 md:h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-5 text-xl md:text-2xl font-bold">2</div>
              <h3 className="font-bold text-gray-900 text-lg md:text-xl mb-3">Pattern Analysis</h3>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed">Our system analyses patterns—tone, clarity, emotional signals and responsiveness. We analyse <em>behaviour</em>, not content.</p>
            </div>
            {/* Step 3 */}
            <div>
              <div className="w-14 h-14 md:w-16 md:h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-5 text-xl md:text-2xl font-bold">3</div>
              <h3 className="font-bold text-gray-900 text-lg md:text-xl mb-3">Personal Insight</h3>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed">Individuals receive a private profile highlighting strengths, blind spots, and practical guidance.</p>
            </div>
          </div>
        </section>

        {/* Section: Is vs Is Not */}
        <section className="p-12 md:p-16 lg:p-20 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12 max-w-5xl mx-auto">
            {/* Is Not */}
            <div className="bg-gray-50 p-8 md:p-10 rounded-xl border border-gray-100">
              <h3 className="text-gray-400 font-semibold uppercase tracking-wider text-base md:text-lg mb-5">SolasLite is NOT</h3>
              <ul className="cross-list space-y-3 text-base md:text-lg">
                <li>A leadership development program</li>
                <li>A personality test</li>
                <li>A generic survey</li>
                <li>A psychometric label</li>
                <li>A performance score</li>
              </ul>
            </div>
            {/* Is */}
            <div className="bg-blue-50 p-8 md:p-10 rounded-xl border border-blue-100 shadow-sm">
              <h3 className="text-blue-600 font-semibold uppercase tracking-wider text-base md:text-lg mb-5">SolasLite IS</h3>
              <ul className="check-list space-y-3 text-gray-800 text-base md:text-lg font-medium">
                <li>A clear picture of leadership traits</li>
                <li>Objective insight from real communication</li>
                <li>A practical baseline for identifying gaps</li>
                <li>An insight layer to support coaching</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section: Value Proposition */}
        <section className="p-12 md:p-16 lg:p-20 bg-gray-50 border-t border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 max-w-6xl mx-auto">
            {/* Individual */}
            <div>
              <div className="flex items-center gap-3 mb-5">
                <i className="fas fa-user text-blue-600 text-xl"></i>
                <h3 className="font-bold text-gray-900 text-xl md:text-2xl">For The Individual</h3>
              </div>
              <ul className="space-y-4 text-base md:text-lg text-gray-600">
                <li><strong className="text-gray-900">Clear Understanding:</strong> See your actual leadership style mirrored back to you.</li>
                <li><strong className="text-gray-900">Pressure Insight:</strong> Understand exactly how your behaviour shifts under stress.</li>
                <li><strong className="text-gray-900">Actionable Guidance:</strong> Receive feedback you can act on immediately.</li>
              </ul>
            </div>
            {/* Organisation */}
            <div>
              <div className="flex items-center gap-3 mb-5">
                <i className="fas fa-building text-blue-600 text-xl"></i>
                <h3 className="font-bold text-gray-900 text-xl md:text-2xl">For The Organisation</h3>
              </div>
              <ul className="space-y-4 text-base md:text-lg text-gray-600">
                <li><strong className="text-gray-900">Early Indicators:</strong> Spot friction points linked to stress early.</li>
                <li><strong className="text-gray-900">Better Coaching:</strong> Move from opinion-led feedback to evidence-informed dialogue.</li>
                <li><strong className="text-gray-900">Reduced Defensiveness:</strong> Feedback is objective data, not personal opinion.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section: Where SolasLite Fits */}
        <section className="p-12 md:p-16 lg:p-20 bg-white border-t border-gray-100">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-10 md:mb-14 text-center">Where SolasLite Fits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12 max-w-5xl mx-auto">
            {/* Core Programmes */}
            <div className="bg-indigo-50 p-8 md:p-10 rounded-xl border border-indigo-100">
              <h3 className="font-bold text-indigo-900 text-lg md:text-xl mb-6 flex items-center gap-3">
                <i className="fas fa-layer-group text-indigo-600 text-xl"></i> Core Programmes
              </h3>
              <ul className="space-y-3 text-base md:text-lg text-gray-700">
                <li className="flex items-start gap-3"><i className="fas fa-check text-indigo-400 mt-1.5 text-lg"></i> Leadership Development</li>
                <li className="flex items-start gap-3"><i className="fas fa-check text-indigo-400 mt-1.5 text-lg"></i> Coaching & Mentoring</li>
                <li className="flex items-start gap-3"><i className="fas fa-check text-indigo-400 mt-1.5 text-lg"></i> Management Training</li>
                <li className="flex items-start gap-3"><i className="fas fa-check text-indigo-400 mt-1.5 text-lg"></i> Organisational Change</li>
              </ul>
            </div>
            {/* Educational Settings */}
            <div className="bg-indigo-50 p-8 md:p-10 rounded-xl border border-indigo-100">
              <h3 className="font-bold text-indigo-900 text-lg md:text-xl mb-6 flex items-center gap-3">
                <i className="fas fa-graduation-cap text-indigo-600 text-xl"></i> Educational Settings
              </h3>
              <ul className="space-y-3 text-base md:text-lg text-gray-700">
                <li className="flex items-start gap-3"><i className="fas fa-check text-indigo-400 mt-1.5 text-lg"></i> MBA Programmes</li>
                <li className="flex items-start gap-3"><i className="fas fa-check text-indigo-400 mt-1.5 text-lg"></i> Executive Education</li>
                <li className="flex items-start gap-3"><i className="fas fa-check text-indigo-400 mt-1.5 text-lg"></i> Professional Qualifications</li>
              </ul>
            </div>
          </div>
          <p className="text-center text-gray-500 text-base md:text-lg mt-10 md:mt-12 italic max-w-3xl mx-auto">"It provides the behavioural baseline that theory alone cannot."</p>
        </section>

        {/* New Section: How Organisations Start (Get Started) */}
        <section className="p-12 md:p-16 lg:p-20 bg-slate-50 border-t border-gray-200">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-10 md:mb-14 text-center">How Organisations Typically Start</h2>
          <div className="flex flex-col md:flex-row justify-center items-center gap-8 md:gap-10 max-w-6xl mx-auto">
            {/* Step 1 */}
            <div className="flex-1 text-center max-w-sm">
              <div className="text-xl md:text-2xl font-bold text-blue-600 mb-4">1. Start Small</div>
              <div className="bg-white p-6 md:p-8 rounded-lg border border-gray-200 shadow-sm h-full">
                <p className="text-base md:text-lg text-gray-700">Pilot SolasLite with a specific cohort or programme.</p>
              </div>
            </div>
            {/* Arrow */}
            <div className="hidden md:block text-gray-300 text-2xl"><i className="fas fa-chevron-right"></i></div>
            {/* Step 2 */}
            <div className="flex-1 text-center max-w-sm">
              <div className="text-xl md:text-2xl font-bold text-blue-600 mb-4">2. Build Clarity</div>
              <div className="bg-white p-6 md:p-8 rounded-lg border border-gray-200 shadow-sm h-full">
                <p className="text-base md:text-lg text-gray-700">Use insight to shape development conversations and priorities.</p>
              </div>
            </div>
            {/* Arrow */}
            <div className="hidden md:block text-gray-300 text-2xl"><i className="fas fa-chevron-right"></i></div>
            {/* Step 3 */}
            <div className="flex-1 text-center max-w-sm">
              <div className="text-xl md:text-2xl font-bold text-blue-600 mb-4">3. Extend Naturally</div>
              <div className="bg-white p-6 md:p-8 rounded-lg border border-gray-200 shadow-sm h-full">
                <p className="text-base md:text-lg text-gray-700">Repeat analysis over time to evidence growth and improvement.</p>
              </div>
            </div>
          </div>
        </section>

        {/* New Section: Real World Use Cases (Proof) */}
        <section className="p-12 md:p-16 lg:p-20 bg-white border-t border-gray-100">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-10 md:mb-14 text-center">Real World Use Cases</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 max-w-6xl mx-auto">
            {/* Case 1 */}
            <div className="bg-gray-50 p-8 md:p-10 rounded-xl">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mb-5 text-xl md:text-2xl">
                <i className="fas fa-globe-americas"></i>
              </div>
              <h3 className="font-bold text-gray-900 text-base md:text-lg mb-2">Workforce Localisation</h3>
              <p className="text-xs md:text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Oil & Gas</p>
              <p className="text-base md:text-lg text-gray-600">Supporting national leaders stepping into roles historically held by expatriates, reducing derailment risk.</p>
            </div>
            {/* Case 2 */}
            <div className="bg-gray-50 p-8 md:p-10 rounded-xl">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mb-5 text-xl md:text-2xl">
                <i className="fas fa-chess-knight"></i>
              </div>
              <h3 className="font-bold text-gray-900 text-base md:text-lg mb-2">Strategic Pivots</h3>
              <p className="text-xs md:text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Contracting & EPC</p>
              <p className="text-base md:text-lg text-gray-600">Ensuring behavioural alignment during diversification and transformation programmes.</p>
            </div>
            {/* Case 3 */}
            <div className="bg-gray-50 p-8 md:p-10 rounded-xl">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mb-5 text-xl md:text-2xl">
                <i className="fas fa-sync-alt"></i>
              </div>
              <h3 className="font-bold text-gray-900 text-base md:text-lg mb-2">Leadership Development</h3>
              <p className="text-xs md:text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Current & future leaders</p>
              <p className="text-base md:text-lg text-gray-600">Providing guidance on communication impact and development to candidates throughout their degree level programme.</p>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 text-white p-12 md:p-16 lg:p-20 text-center">
          <h4 className="uppercase tracking-widest text-sm md:text-base font-semibold text-slate-400 mb-8">Built on Trust & Privacy</h4>
          
          <div className="flex flex-wrap justify-center gap-8 md:gap-10 mb-10 text-base md:text-lg text-slate-300">
            <span className="flex items-center gap-3"><i className="fas fa-lock text-emerald-400 text-xl"></i> Private (Individual owns insight)</span>
            <span className="flex items-center gap-3"><i className="fas fa-shield-alt text-emerald-400 text-xl"></i> Safe (No ranking/judgment)</span>
            <span className="flex items-center gap-3"><i className="fas fa-server text-emerald-400 text-xl"></i> Secure (ISO 27001)</span>
          </div>

          <div className="border-t border-slate-800 pt-10 mb-8">
            <p className="text-slate-400 text-base md:text-lg italic mb-3">Ready to see it in action?</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="https://youtu.be/czoChy5wExg" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-blue-600 hover:bg-blue-500 text-white font-bold py-4 px-8 text-base md:text-lg rounded-full transition-colors duration-200">
              <i className="fas fa-play"></i> Watch Explainer Video
            </a>
            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center gap-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-4 px-8 text-base md:text-lg rounded-full transition-colors duration-200"
            >
              <i className="fas fa-user-plus"></i> Request Access
            </button>
          </div>
        </footer>

      </div>

      {/* HubSpot Form Modal */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
              aria-label="Close modal"
            >
              <i className="fas fa-times text-2xl"></i>
            </button>

            {/* Modal content */}
            <div className="p-8 md:p-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Request Access to SolasLite</h2>              
              {/* HubSpot form using hs-form-frame */}
              <div 
                className="hs-form-frame" 
                data-region="na1" 
                data-form-id="b0837158-32e1-471b-b454-17ff63830aec" 
                data-portal-id="49566702"
              ></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Marketing;
